/**
 * LeftShield Portal - Run Detail Page
 * Route: /runs/:referenceId | Category: Compliance
 *
 * §12746 Wave 2 — full GUID-routed detail page for `Run`, replacing the local
 * `RunDetailDrawer` on `PolicyRunsPage` for direct-link / deep-link access. Route uses
 * `/runs/:referenceId` — matching `PolicyRunsPage`'s actual mounted route (`/runs`; the nav
 * item is labelled "Decision Log" but the path is `/runs`). Shape follows the reference
 * migration `src/pages/AIDecisionDetailPage.tsx`.
 *
 * FEATURE-PORTING PARITY — every section of the retired `RunDetailDrawer` (PolicyRunsPage.tsx)
 * is ported below:
 *   - Status badge                              → Run Summary card, DetailBadges
 *   - Name / Target / Trigger / Framework /
 *     Duration / Started metadata grid          → Run Summary card, DetailMetadataGrid
 *   - Findings badges (critical/high/medium/
 *     low + total)                              → Run Summary card, DetailBadges
 *   - Error message banner                      → Run Summary card, conditional banner
 *   - Scan Results DataGrid (sensor/status/
 *     findings/duration)                        → Scan Results card, DataGrid
 *   - Cancel Run button (Running/Pending only)   → Scan Results card footer, cancel mutation
 *
 * Nothing from `RunDetailDrawer` was omitted — the drawer had no fields beyond the above.
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  Badge,
  Button,
  Spinner,
  tokens,
  makeStyles,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
} from '@fluentui/react-components';
import { ArrowSyncRegular, ErrorCircleRegular, DismissCircleRegular } from '@fluentui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageStyles } from '../../styles/page-styles';
import { PageHeader } from '../../components/common/PageHeader';
import {
  DetailSection,
  DetailField,
  DetailMetadataGrid,
  DetailBadges,
} from '../../components/common/DetailDrawer';
import { runsApi } from '../../services/api';
import type { Run, ScanResult, RunStatus } from '../../types';

/** Compliance Cyan — `brand/brand-config.json` → `colors.categoryThemes.compliance.primary`. */
const RUN_COLOR = '#13B5B1';

const statusColor = (status: RunStatus): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Failed':
      return 'danger';
    case 'Running':
      return 'informative';
    case 'Cancelled':
      return 'warning';
    case 'Pending':
    default:
      return 'warning';
  }
};

const formatDuration = (iso?: string): string => {
  if (!iso) return '-';
  // Basic ISO 8601 duration handling (e.g. "00:02:34" or "PT2M34S")
  if (iso.startsWith('PT')) {
    const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (m) {
      const h = m[1] ? `${m[1]}h ` : '';
      const min = m[2] ? `${m[2]}m ` : '';
      const s = m[3] ? `${m[3]}s` : '';
      return `${h}${min}${s}`.trim() || '-';
    }
  }
  return iso;
};

const useLocalStyles = makeStyles({
  centeredState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  findingsRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
});

export const RunDetailPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();
  const { referenceId } = useParams<{ referenceId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: run,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['run-detail', referenceId],
    queryFn: () => runsApi.getByReferenceId(referenceId!),
    enabled: !!referenceId,
  });

  const { data: scanResults, isLoading: isScanResultsLoading } = useQuery({
    queryKey: ['runScanResults', run?.id],
    queryFn: () => runsApi.getScanResults(run!.id),
    enabled: !!run,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => runsApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policyRuns'] });
      queryClient.invalidateQueries({ queryKey: ['policy-runs-grid'] });
      queryClient.invalidateQueries({ queryKey: ['run-detail', referenceId] });
    },
  });

  const backAction = (
    <Button appearance="subtle" onClick={() => navigate('/runs')}>
      Back to Decision Log
    </Button>
  );

  const scanColumns: TableColumnDefinition<ScanResult>[] = [
    createTableColumn<ScanResult>({
      columnId: 'type',
      renderHeaderCell: () => 'Sensor',
      renderCell: (item) => <Badge appearance="outline">{item.type}</Badge>,
    }),
    createTableColumn<ScanResult>({
      columnId: 'status',
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <Badge appearance="filled" color={statusColor(item.status)}>
          {item.status}
        </Badge>
      ),
    }),
    createTableColumn<ScanResult>({
      columnId: 'findings',
      renderHeaderCell: () => 'Findings',
      renderCell: (item) => (
        <div className={ls.findingsRow}>
          {item.criticalFindings > 0 && (
            <Badge appearance="tint" color="danger">{item.criticalFindings} C</Badge>
          )}
          {item.highFindings > 0 && (
            <Badge appearance="tint" color="danger">{item.highFindings} H</Badge>
          )}
          <Body2>{item.totalFindings} total</Body2>
        </div>
      ),
    }),
    createTableColumn<ScanResult>({
      columnId: 'duration',
      renderHeaderCell: () => 'Duration',
      renderCell: (item) => <Body2>{formatDuration(item.duration)}</Body2>,
    }),
  ];

  if (!referenceId) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Run Detail"
          subtitle="No run reference ID was provided."
          icon={<ArrowSyncRegular />}
          iconColor={RUN_COLOR}
          actions={backAction}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Run Detail"
          subtitle="Loading policy run…"
          icon={<ArrowSyncRegular />}
          iconColor={RUN_COLOR}
        />
        <Card>
          <div className={ls.centeredState}>
            <Spinner label="Loading run…" />
          </div>
        </Card>
      </div>
    );
  }

  if (isError || !run) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Run Detail"
          subtitle={`Run ${referenceId}`}
          icon={<ArrowSyncRegular />}
          iconColor={RUN_COLOR}
          actions={backAction}
        />
        <Card>
          <div className={ls.centeredState}>
            <ErrorCircleRegular style={{ fontSize: '32px', color: tokens.colorPaletteRedForeground1 }} />
            <Body1>Unable to load this run. It may not exist, or you may not have access to it.</Body1>
            <Button appearance="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const r: Run = run;
  const canCancel = r.status === 'Running' || r.status === 'Pending';

  return (
    <div className={ps.page}>
      <PageHeader
        title="Run Detail"
        subtitle={`${r.targetIdentifier} — ${r.frameworkCode}`}
        icon={<ArrowSyncRegular />}
        iconColor={RUN_COLOR}
        actions={backAction}
      />

      {/* Run Summary — ported from RunDetailDrawer: status badge, metadata grid, findings badges, error banner */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>{r.name}</Title2>
            <DetailBadges>
              <Badge appearance="filled" color={statusColor(r.status)}>
                {r.status}
              </Badge>
            </DetailBadges>
          </div>

          <DetailMetadataGrid>
            <DetailField label="Name">
              <Body1 style={{ fontWeight: 500 }}>{r.name}</Body1>
            </DetailField>
            <DetailField label="Target">
              <Body1>{r.targetIdentifier}</Body1>
            </DetailField>
            <DetailField label="Trigger">
              <Badge appearance="outline">{r.trigger}</Badge>
            </DetailField>
            <DetailField label="Framework">
              <Badge appearance="outline">{r.frameworkCode}</Badge>
            </DetailField>
            <DetailField label="Duration">
              <Body1>{formatDuration(r.duration)}</Body1>
            </DetailField>
            <DetailField label="Started">
              <Body1>{r.startedAt ? new Date(r.startedAt).toLocaleString() : '-'}</Body1>
            </DetailField>
          </DetailMetadataGrid>

          <DetailBadges>
            <Badge appearance="tint" color="danger">{r.criticalFindings} Critical</Badge>
            <Badge appearance="tint" color="danger">{r.highFindings} High</Badge>
            <Badge appearance="tint" color="warning">{r.mediumFindings} Medium</Badge>
            <Badge appearance="tint" color="success">{r.lowFindings} Low</Badge>
            <Body2 style={{ marginLeft: 'auto' }}>{r.totalFindings} total findings</Body2>
          </DetailBadges>

          {r.errorMessage && (
            <div className={ls.errorBanner}>
              <Body2>{r.errorMessage}</Body2>
            </div>
          )}
        </div>
      </Card>

      {/* Scan Results — ported from RunDetailDrawer's DataGrid + Cancel Run button */}
      <Card>
        <div className={ps.cardBody}>
          <DetailSection title="Scan Results">
            {isScanResultsLoading ? (
              <div className={ls.centeredState}>
                <Spinner size="medium" label="Loading scan results..." />
              </div>
            ) : scanResults && scanResults.length > 0 ? (
              <DataGrid items={scanResults} columns={scanColumns}>
                <DataGridHeader>
                  <DataGridRow>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<ScanResult>>
                  {({ item, rowId }) => (
                    <DataGridRow<ScanResult> key={rowId}>
                      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            ) : (
              <Body1
                style={{
                  textAlign: 'center',
                  padding: tokens.spacingVerticalXXL,
                  color: tokens.colorNeutralForeground3,
                }}
              >
                No scan results available for this run.
              </Body1>
            )}
          </DetailSection>

          {canCancel && (
            <Button
              appearance="secondary"
              icon={<DismissCircleRegular />}
              onClick={() => cancelMutation.mutate(r.id)}
              disabled={cancelMutation.isPending}
            >
              Cancel Run
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
